import { useCallback, useEffect, useMemo, useState } from "react";
import { Star, StarOff, Trash2, UserCheck, UserX } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, ApiError, type AdminUser } from "../../lib/api";
import { canAccess } from "../../lib/auth-tokens";
import { AdminFilterBar, AdminPageHeader, AdminTabBar } from "../../components/admin/AdminUI";
import { ConfirmDialog } from "../../components/admin/AdminModals";
import { StudentEditModal } from "../../components/admin/StudentEditModal";
import { StudentPreviewModal } from "../../components/admin/StudentPreviewModal";
import { useAdminToken, PageError } from "./adminShared";
import { UsersList } from "../../components/admin/UsersList";
import { BulkBar } from "../../components/admin/dataTable/BulkBar";
import { FilterBar } from "../../components/admin/dataTable/FilterBar";
import { useListState } from "../../components/admin/dataTable/useListState";
import { ConfirmDialog as UiConfirmDialog } from "../../components/ui/ConfirmDialog";

export function AdminUsers() {
  const { tokens } = useAuth();
  const token = useAdminToken();
  const canWrite = canAccess(tokens, "users.write");
  const canManageAdmins = canAccess(tokens, "admins.manage");
  const [promoteTarget, setPromoteTarget] = useState<AdminUser | null>(null);
  const [promoteSuccess, setPromoteSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [planTab, setPlanTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [editUserTab, setEditUserTab] = useState<"profile" | "logs">("profile");
  const [previewUser, setPreviewUser] = useState<AdminUser | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<AdminUser | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const list = useListState("us", [], { key: "joined", dir: "desc" });

  // Simple sort: newest / oldest, name, and most / least active by quizzes, past exams or COC.
  const visibleUsers = useMemo(() => {
    const dir = list.sortDir === "asc" ? 1 : -1;
    const num = (u: AdminUser) => {
      switch (list.sortKey) {
        case "quizzes": return u.quizzes ?? 0;
        case "exams": return u.exams ?? 0;
        case "cocs": return u.cocs ?? 0;
        case "activity": return u.activity ?? 0;
        case "joined": return Date.parse(u.joined) || 0;
        default: return null;
      }
    };
    const rows = [...users];
    if (list.sortKey === "name") rows.sort((a, b) => a.name.localeCompare(b.name) * dir);
    else if (num(rows[0] ?? ({} as AdminUser)) !== null) rows.sort((a, b) => ((num(a) as number) - (num(b) as number)) * dir);
    return rows;
  }, [users, list.sortKey, list.sortDir]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    api.admin.users(token, { search: debounced, plan: planTab, status: statusFilter })
      .then((data) => {
        setUsers(data.items);
        setTotal(data.total);
        setCounts(data.counts ?? {});
        setSelectedUsers(new Set());
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load users"))
      .finally(() => setLoading(false));
  }, [token, debounced, planTab, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const hasFilters = Boolean(search || statusFilter !== "all");

  async function runBulkUserAction(action: "deactivate" | "activate" | "grant_premium" | "revoke_premium" | "delete") {
    if (!token || selectedUsers.size === 0) return;
    setActionLoading("bulk");
    try {
      await api.admin.bulkUsersAction(token, {
        action,
        user_ids: Array.from(selectedUsers),
      });
      setSelectedUsers(new Set());
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Bulk action failed");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleAction(userId: string, action: "suspend" | "activate" | "premium" | "free" | "delete" | "edit" | "promote" | "logs" | "preview") {
    if (!token) return;
    const user = users.find((u) => u.id === userId);
    if (action === "preview") {
      if (user) setPreviewUser(user);
      return;
    }
    if (action === "logs") {
      if (user) {
        setEditUser(user);
        setEditUserTab("logs");
      }
      return;
    }
    if (action === "edit") {
      if (user) {
        setEditUser(user);
        setEditUserTab("profile");
      }
      return;
    }
    if (action === "delete") {
      if (user) setConfirmDeactivate(user);
      return;
    }
    if (action === "promote") {
      if (user) setPromoteTarget(user);
      return;
    }
    setActionLoading(userId);

    try {
      await api.admin.updateUser(token, userId, {
        is_active: action === "suspend" ? false : action === "activate" ? true : undefined,
        is_premium: action === "premium" ? true : action === "free" ? false : undefined,
      });
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Action failed");
    } finally {
      setActionLoading(null);
    }
  }

  async function confirmPromoteUser() {
    if (!token || !promoteTarget) return;
    setActionLoading(promoteTarget.id);
    try {
      const res = await api.admin.promoteUser(token, promoteTarget.id);
      setPromoteSuccess(
        `Successfully promoted ${promoteTarget.name} to administrator. ${
          res.pendingSetup ? "Credential setup is pending password enrollment via Telegram OTP." : ""
        }`
      );
      setPromoteTarget(null);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Promotion failed");
      setPromoteTarget(null);
    } finally {
      setActionLoading(null);
    }
  }

  async function confirmDeactivateUser() {
    if (!token || !confirmDeactivate) return;
    setActionLoading(confirmDeactivate.id);
    try {
      await api.admin.deleteUser(token, confirmDeactivate.id);
      setConfirmDeactivate(null);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Deactivate failed");
      setConfirmDeactivate(null);
    } finally {
      setActionLoading(null);
    }
  }

  return (
    <div className="p-8 space-y-6">
      <AdminPageHeader
        title="Users"
        subtitle={`${counts.all ?? total} registered students · manage plans, access, and accounts`}
      />
      {error && <PageError message={error} />}
      {!canWrite && (
        <p className="text-sm text-warning bg-warning/10 border border-warning/30 rounded-lg px-4 py-3">
          You have read-only access to students. Ask a super admin for edit permission.
        </p>
      )}

      <AdminTabBar
        active={planTab}
        onChange={setPlanTab}
        tabs={[
          { id: "all", label: "All students", count: counts.all },
          { id: "premium", label: "Premium", count: counts.premium },
          { id: "free", label: "Free", count: counts.free },
        ]}
      />

      {editUser && token ? (
        <StudentEditModal
          open={true}
          user={editUser}
          token={token}
          onClose={() => setEditUser(null)}
          onSaved={load}
          initialTab={editUserTab}
          inline={true}
        />
      ) : (
        <>
          <AdminFilterBar
            search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name or phone…"
        selects={[
          {
            id: "status",
            label: "Account status",
            value: statusFilter,
            options: [
              { value: "all", label: "All statuses" },
              { value: "active", label: "Active" },
              { value: "suspended", label: "Suspended" },
            ],
          },
        ]}
        onSelectChange={(_, v) => setStatusFilter(v)}
        showClear={hasFilters}
        onClear={() => { setSearch(""); setStatusFilter("all"); }}
      />

      <FilterBar
        list={list}
        filters={[]}
        sortOptions={[
          { key: "joined", label: "Newest first", dir: "desc" },
          { key: "joined", label: "Oldest first", dir: "asc" },
          { key: "name", label: "Name A to Z", dir: "asc" },
          { key: "name", label: "Name Z to A", dir: "desc" },
          { key: "activity", label: "Most active", dir: "desc" },
          { key: "activity", label: "Least active", dir: "asc" },
          { key: "quizzes", label: "Most quizzes", dir: "desc" },
          { key: "quizzes", label: "Fewest quizzes", dir: "asc" },
          { key: "exams", label: "Most past exams", dir: "desc" },
          { key: "exams", label: "Fewest past exams", dir: "asc" },
          { key: "cocs", label: "Most COC", dir: "desc" },
          { key: "cocs", label: "Fewest COC", dir: "asc" },
        ]}
      />

      <p className="text-sm text-muted-foreground">
        Showing <span className="font-semibold text-foreground">{visibleUsers.length}</span> of {total} students
      </p>

      <UsersList
        rows={visibleUsers}
        list={list}
        loading={loading}
        selected={selectedUsers}
        onSelectedChange={setSelectedUsers}
        canWrite={canWrite}
        canManageAdmins={canManageAdmins}
        onAction={handleAction}
      />

      {canWrite && (
        <BulkBar
          count={selectedUsers.size}
          noun="students"
          busy={!!actionLoading}
          total={visibleUsers.length}
          onSelectAll={() => setSelectedUsers(new Set(visibleUsers.map((u) => u.id)))}
          onClear={() => setSelectedUsers(new Set())}
          actions={[
            { key: "grant", label: "Grant premium", icon: <Star size={14} />, onClick: () => void runBulkUserAction("grant_premium") },
            { key: "revoke", label: "Remove premium", icon: <StarOff size={14} />, onClick: () => void runBulkUserAction("revoke_premium") },
            { key: "activate", label: "Activate", icon: <UserCheck size={14} />, onClick: () => void runBulkUserAction("activate") },
            { key: "deactivate", label: "Suspend", icon: <UserX size={14} />, onClick: () => void runBulkUserAction("deactivate") },
            { key: "delete", label: "Delete", icon: <Trash2 size={14} />, danger: true, onClick: () => setBulkDeleteOpen(true) },
          ]}
        />
      )}
      <UiConfirmDialog
        isOpen={bulkDeleteOpen}
        title={`Delete ${selectedUsers.size} students?`}
        message="Their accounts are removed. Suspend them instead if you may need them again."
        variant="destructive"
        confirmText="Delete"
        loading={!!actionLoading}
        onConfirm={async () => {
          setBulkDeleteOpen(false);
          await runBulkUserAction("delete");
        }}
        onCancel={() => setBulkDeleteOpen(false)}
      />

      {promoteSuccess && (
        <div className="bg-success/10 border border-success/30 text-success rounded-lg p-3 text-sm flex items-center justify-between">
          <span>{promoteSuccess}</span>
          <button type="button" onClick={() => setPromoteSuccess(null)} className="text-success hover:text-success font-bold ml-2">×</button>
        </div>
      )}
        </>
      )}


      <ConfirmDialog
        open={!!confirmDeactivate}
        title="Deactivate student?"
        message={`Suspend ${confirmDeactivate?.name}? They will lose access until reactivated.`}
        confirmLabel="Deactivate"
        variant="destructive"
        loading={!!actionLoading}
        onClose={() => setConfirmDeactivate(null)}
        onConfirm={confirmDeactivateUser}
      />

      <ConfirmDialog
        open={!!promoteTarget}
        title="Promote student to administrator?"
        message={`Promote ${promoteTarget?.name} (${promoteTarget?.phone}) to administrator? Their backend role will update to Administrator. If they do not already have an administrator password, credential setup will be pending until they complete secure Telegram identity verification and password enrollment.`}
        confirmLabel="Promote to Admin"
        loading={!!actionLoading}
        onClose={() => setPromoteTarget(null)}
        onConfirm={confirmPromoteUser}
      />
      {previewUser && <StudentPreviewModal user={previewUser} onClose={() => setPreviewUser(null)} />}
    </div>
  );
}
